<script setup lang="ts">
  import { TypoVariant, Typo } from '@travelata-libs/ui-kit';
  import { pluralize } from '@travelata-libs/utils';
  import { computed } from 'vue';
  import { RatingSize } from './Rating.types';

  interface RatingProps {
    size?: `${RatingSize}`;
    clickable?: boolean;
    rating?: number;
    reviewsCount?: number;
    reviewsLabels?: string[];
  }

  const props = withDefaults(defineProps<RatingProps>(), {
    size: RatingSize.M,
    clickable: false,
    rating: 0,
    reviewsCount: 0,
    reviewsLabels: () => ['отзыв', 'отзыва', 'отзывов'],
  });

  interface RatingEmits {
    (e: 'click'): void;
  }

  const emit = defineEmits<RatingEmits>();

  const ratingVariant = computed(() => {
    const typeVariants: Record<RatingSize, TypoVariant> = {
      [RatingSize.M]: TypoVariant.Heading6,
      [RatingSize.L]: TypoVariant.Heading5,
    };

    return typeVariants[props.size];
  });

  const reviewsVariant = computed(() => {
    const typeVariants: Record<RatingSize, TypoVariant> = {
      [RatingSize.M]: TypoVariant.BodyMRegular,
      [RatingSize.L]: TypoVariant.BodyLRegular,
    };

    return typeVariants[props.size];
  });

  const ratingLevel = computed(() => {
    if (props.rating >= 4) return 'best';

    if (props.rating >= 3 && props.rating < 4) return 'good';

    if (props.rating >= 1 && props.rating < 3) return 'fine';

    return 'bad';
  });

  const ratingText = computed(() => {
    return props.rating
      ? new Intl.NumberFormat('ru-RU', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }).format(props.rating)
      : '-';
  });

  const reviewsCountText = computed(() => {
    const pluralizedReviewsCount = pluralize(props.reviewsLabels, props.reviewsCount, false);
    const formattedReviewsCount = new Intl.NumberFormat('ru-RU', {}).format(props.reviewsCount);

    return props.reviewsCount ? `${formattedReviewsCount} ${pluralizedReviewsCount}` : 'Нет отзывов';
  });

  const onClick = () => {
    if (!props.reviewsCount || !props.clickable) return;

    emit('click');
  };
</script>

<template>
  <div
    :class="[
      'tl-rating',
      `tl-rating_size-${size}`,
      `tl-rating_${ratingLevel}`,
      {
        'tl-rating_empty-reviews': !reviewsCount,
        'tl-rating_clickable': clickable,
      },
    ]"
    data-test-id="root"
    @click="onClick"
  >
    <div class="tl-rating__wrapper">
      <Typo class="tl-rating__rating" :variant="ratingVariant" data-test-id="rating">
        {{ ratingText }}
      </Typo>

      <div class="tl-rating__reviews">
        <Typo :variant="reviewsVariant" data-test-id="reviews">{{ reviewsCountText }}</Typo>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .tl-rating {
    $root: &;

    cursor: default;
    display: flex;
    gap: var(--spacing-xs);

    &_bad {
      #{$root}__rating {
        background-color: var(--color-text-tertiary);
      }

      #{$root}__wrapper {
        background-color: var(--color-slate-10);
      }
    }

    &_fine {
      #{$root}__rating {
        background-color: var(--color-orange-50);
      }

      #{$root}__wrapper {
        background-color: var(--color-orange-05);
      }
    }

    &_good {
      #{$root}__rating {
        background-color: var(--color-lime-50);
      }

      #{$root}__wrapper {
        background-color: var(--color-lime-05);
      }
    }

    &_best {
      #{$root}__rating {
        background-color: var(--color-green-50);
      }

      #{$root}__wrapper {
        background-color: var(--color-green-05);
      }
    }

    &_size-m {
      height: 36px;

      #{$root}__rating {
        width: 40px;
      }
    }

    &_size-l {
      height: 40px;

      #{$root}__rating {
        width: 48px;
      }
    }

    &_empty-reviews {
      #{$root}__icon {
        fill: var(--color-text-tertiary);
      }
    }

    &_clickable {
      cursor: pointer;
    }

    &__wrapper {
      border-radius: var(--rounding-s);
      display: flex;
    }

    &__rating {
      align-items: center;
      border-radius: var(--rounding-s);
      color: var(--color-text-inverse);
      display: flex;
      justify-content: center;
    }

    &__reviews {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: 0 var(--spacing-xs);
    }

    &__icon {
      fill: var(--color-text-secondary);
      margin-bottom: var(--spacing-xxxs);
    }
  }
</style>
